import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  IOptioningData,
  IPreviewQuestionAnswerDetailsSaved,
  IQuesField,
} from './multi-section-form-preview.models';
import { MultiSectionFormPreviewService } from './multi-section-form-preview.service';
import { ValidateResponseService } from './validate-response.service';
import { checkAndFormatDate } from '../../shared/date.utils';

@Injectable({
  providedIn: 'root',
})
export class FieldInputHtmlResponseMapperService {
  public msfps = inject(MultiSectionFormPreviewService);
  public vrs = inject(ValidateResponseService);

  sanitizer = inject(DomSanitizer);

  constructor() {}

  mapResponseIntoFieldUI(
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    // Look up: SPECEFIC FIELD TYPE LOGIC
    let modifiedHtmlStr;
    // map response into UI depending on the type of input field
    if (field?.data[1]?.type?.toLowerCase() === 'dropdown') {
      modifiedHtmlStr = this.mapDropdownInputIntoUI(
        'dropdown',
        field,
        triggerSubjectAction
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'multiselect-dropdown') {
      modifiedHtmlStr = this.mapDropdownInputIntoUI(
        'multiselect-dropdown',
        field,
        triggerSubjectAction
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'date') {
      modifiedHtmlStr = this.mapInputIntoUI(
        'DATE',
        field,
        triggerSubjectAction
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'textbox') {
      modifiedHtmlStr = this.mapInputIntoUI(
        'NUMBER',
        field,
        triggerSubjectAction
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'radio') {
      modifiedHtmlStr = this.mapRadioInputIntoUI(field, triggerSubjectAction);
    } else if (field?.data[1]?.type?.toLowerCase() === 'checkbox') {
      modifiedHtmlStr = this.mapCheckboxInputIntoUI(
        field,
        triggerSubjectAction
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'textarea') {
      modifiedHtmlStr = this.mapTextAreaInputIntoUI(
        field,
        triggerSubjectAction
      );
    }else if (field?.data[1]?.type?.toLowerCase() === 'file') {
      modifiedHtmlStr = this.mapInputIntoUI(
        'FILE',
        field,
        triggerSubjectAction
      );
    }
    return modifiedHtmlStr;
  }

  mapDropdownInputIntoUI(
    dropdownType: 'dropdown' | 'multiselect-dropdown',
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('select'))?.forEach(
      (selectEleTarget) => {
        if (dropdownType === 'dropdown') {
          this.mapSingleSelectDropdownInputIntoUI(
            selectEleTarget,
            field,
            triggerSubjectAction
          );
        } else if (dropdownType === 'multiselect-dropdown') {
          this.mapMultiSelectDropdownInputIntoUI(
            selectEleTarget,
            field,
            triggerSubjectAction
          );
        }
      }
    );

    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  mapSingleSelectDropdownInputIntoUI(
    eventTarget: HTMLSelectElement,
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);
    // 1. Find the selected dropdown HTML equivalent from that of Field OBJ
    const selectedOptionEle = optionsArr.find(
      (option: HTMLOptionElement) =>
        option.value ===
        field.data[1].dropdowndata?.find(
          (dropdownData: IOptioningData) => dropdownData.selected
        )?.optionid
    );

    const selection = selectedOptionEle ? selectedOptionEle.text : '';

    // 2. Reset dropdown
    optionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;

      if (this.msfps.mode === 'previewDisabled') {
        option.setAttribute('disabled', 'true');
        option.disabled = true;
      }
    });

    // 3. if the looping HTML Input Radio Element - is the current radio (ie., checked radio)
    if (selectedOptionEle && selection.toLowerCase() !== 'select option') {
      //apply selected attribute to the selected option
      selectedOptionEle.setAttribute('selected', 'true');
      selectedOptionEle.selected = true;

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(eventTarget.id.replace('Dropdown_', '').toString()) || 'null'
      );

      const latestFieldAnswerValue =
        selection.toLowerCase() !== 'select option' ? [selection] : null;

      const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.value],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('Dropdown_', '').toString(),
        JSON.stringify(fieldAnswer)
      );

      if (triggerSubjectAction === 'setAction') {
        this.msfps.helper$.next({
          activeQuestionId: eventTarget.id.replace('Dropdown_', '').toString(),
          val: 'set on preview question input element',
        });
      }
    }
  }

  mapMultiSelectDropdownInputIntoUI(
    multiSelectTarget: HTMLSelectElement,
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const allOptionsArr = Array.from(
      multiSelectTarget.options as HTMLOptionsCollection
    );

    const optionsSelected = field.data[1].multiselectDropdowndata?.filter(
      (dropdownData: IOptioningData) => dropdownData.selected
    );

    const isOptionIdSelected = (option: HTMLOptionElement) =>
      optionsSelected?.map((x) => x.optionid)?.includes(option.value);

    const isOptionValueSelected = (option: HTMLOptionElement) =>
      optionsSelected?.map((x) => x.answer)?.includes(option.innerText);

    allOptionsArr?.forEach((option: HTMLOptionElement) => {
      if (isOptionIdSelected(option) || isOptionValueSelected(option)) {
        const selectedOptionEle = option;
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      } else {
        const unselectedOptionEle = option;
        unselectedOptionEle.selected = false;
        unselectedOptionEle.removeAttribute('selected');
      }

      if (this.msfps.mode === 'previewDisabled') {
        option.setAttribute('disabled', 'true');
        option.disabled = true;
      }
    });

    if (multiSelectTarget.selectedOptions) {
      const selectedOptionsArr = Array.from(
        multiSelectTarget.selectedOptions as HTMLOptionsCollection
      );

      // remove select attribute for all the existing options
      allOptionsArr.forEach((option: HTMLOptionElement) => {
        option.removeAttribute('selected');
        option.selected = false;
      });

      //add select attribute for all the selected options
      selectedOptionsArr.forEach((selectedOptionEle: HTMLOptionElement) => {
        selectedOptionEle.setAttribute('selected', 'true');
        selectedOptionEle.selected = true;
      });

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = multiSelectTarget.outerHTML;

      const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          multiSelectTarget.id.replace('MultiselectDropdown_', '').toString()
        ) || 'null'
      );

      const latestFieldAnswerValue = selectedOptionsArr.map(
        (item) => item.text
      );

      const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: selectedOptionsArr.map((item) => item.value),
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(multiSelectTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };
      localStorage.setItem(
        multiSelectTarget.id.replace('MultiselectDropdown_', '').toString(),
        JSON.stringify(fieldAnswer)
      );

      if (triggerSubjectAction === 'setAction') {
        this.msfps.helper$.next({
          activeQuestionId: multiSelectTarget.id
            .replace('MultiselectDropdown_', '')
            .toString(),
          val: 'set on preview question input element',
        });
      }
    }
  }

  mapRadioInputIntoUI(
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field.data[1].question.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      // 1. Find the selected radio HTML equivalent from that of Field OBJ
      const selectedRadioOptionObj = field.data[1].radiodata?.find(
        (radiodata: IOptioningData) => radiodata.selected
      );
      const isIdChecked =
        eventTarget.value === selectedRadioOptionObj?.optionid;
      const isValueChecked =
        (eventTarget.nextSibling as Text)?.data ===
        selectedRadioOptionObj?.answer;
      const isChecked = isIdChecked || isValueChecked;

      const selection = isChecked ? selectedRadioOptionObj.answer : null;

      // 2. if the looping HTML Input Radio Element - is the current radio (ie., checked radio)
      if (selection && isChecked) {
        // 2.1 apply checked attribute to the selected radio
        eventTarget.setAttribute('checked', 'true');
        eventTarget.checked = true;

        if (this.msfps.mode === 'previewDisabled') {
          eventTarget.setAttribute('disabled', 'true');
          eventTarget.disabled = true;
        }

        // 2.2 Prepare modifiedHTML to put into `field.data[1].question.changingThisBreaksApplicationSecurity` property
        const modifiedFieldEle = document.createElement('div');
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex;justify-content:left'
        );
        modifiedFieldEle.innerHTML = eventTarget.outerHTML;

        // 2.3 Compare the old answer from the localStorage

        const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
          localStorage.getItem(
            eventTarget.name.replace('Radio_', '').toString()
          ) || 'null'
        );

        // and latest answer from the eventTarget
        const latestFieldAnswerValue =
          triggerSubjectAction === 'setAction'
            ? field.nodeLogic?.selectedAnswers ?? null
            : selection
            ? [selection]
            : null;

        // to update isAnswerModified Boolean
        const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
            modifiedFieldEle.outerHTML
          ),
          answerUniqueId: [eventTarget.value],
          answerValue: latestFieldAnswerValue,
          isAnswerValid: Boolean(eventTarget.value),
          isAnswerModified: !(
            oldFieldAnswer?.answerValue?.toString() ===
            latestFieldAnswerValue?.toString()
          ),
        };

        // 2.4 and store into the localStorage (using localStorage.setItem('question_id', fieldAnswer))
        localStorage.setItem(
          eventTarget.name.replace('Radio_', '').toString(),
          JSON.stringify(fieldAnswer)
        );

        if (triggerSubjectAction === 'setAction') {
          this.msfps.helper$.next({
            activeQuestionId: eventTarget.name.replace('Radio_', ''),
            val: 'set on preview question input element',
          });
        }
      } else {
        eventTarget.removeAttribute('checked');
        eventTarget.checked = false;
      }
    });

    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  mapCheckboxInputIntoUI(
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;

      const checkboxWrapperTarget =
        eventTarget?.parentElement?.parentElement?.parentElement;

      if (!checkboxWrapperTarget || !eventTarget.parentElement) {
        return;
      }

      checkboxWrapperTarget.setAttribute(
        'id',
        eventTarget.parentElement.id
          ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
          ?.toString()
      );

      const allCheckboxContainersArr = Array.from(
        checkboxWrapperTarget.children
      );

      const isOptionChecked = (option: HTMLInputElement) => {
        return field.data[1].checkboxdata
          ?.filter((checkboxdata: IOptioningData) => checkboxdata.selected)
          ?.map((x) => x.optionid)
          ?.includes(option.id.replace('Checkbox_', ''));
      };

      const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
        (x) => x?.firstChild?.firstChild as HTMLInputElement
      );

      // First, remove checked from all
      allCheckboxesArr.forEach((option: HTMLInputElement) => {
        option.removeAttribute('checked');
        option.checked = false;
      });

      // Then, set checked only for those selected in data
      allCheckboxesArr.forEach((option: HTMLInputElement) => {
        const optionId = option.id.replace('Checkbox_', '');
        const isChecked = field.data[1].checkboxdata?.some(
          (checkboxdata: IOptioningData) => checkboxdata.optionid === optionId && checkboxdata.selected
        );
        if (isChecked) {
          option.setAttribute('checked', 'true');
          option.checked = true;
          if (this.msfps.mode === 'previewDisabled') {
            option.setAttribute('disabled', 'true');
            option.disabled = true;
          }
        } else {
          option.removeAttribute('checked');
          option.checked = false;
          option.removeAttribute('disabled');
          option.disabled = false;
        }
      });

      if (allCheckboxesArr?.length !== 0) {
        const checkedOptionsArr = allCheckboxesArr.filter((cb) => cb.checked);
        // remove select attribute for all the existing options
        allCheckboxesArr.forEach((option: HTMLInputElement) => {
          option.removeAttribute('checked');
          option.checked = false;
        });

        //add select attribute for all the checked options
        checkedOptionsArr.forEach((checkedOptionEle: HTMLInputElement) => {
          checkedOptionEle.setAttribute('checked', 'true');
          checkedOptionEle.checked = true;
        });

        const modifiedFieldEle = document.createElement('div');
        modifiedFieldEle.setAttribute(
          'style',
          'display:flex;justify-content:left'
        );
        modifiedFieldEle.innerHTML = checkboxWrapperTarget.outerHTML;

        const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
          localStorage.getItem(
            checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString()
          ) || 'null'
        );

        const latestFieldAnswerValue = checkedOptionsArr.map(
          (item) => (item.nextSibling as Text).data
        );

        const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
          modifiedField: this.sanitizer.bypassSecurityTrustHtml(
            modifiedFieldEle.outerHTML
          ),
          answerUniqueId: checkedOptionsArr.map((item) => item.id),
          answerValue: latestFieldAnswerValue,
          isAnswerValid: checkedOptionsArr.length > 0,
          isAnswerModified: !(
            oldFieldAnswer?.answerValue?.toString() ===
            latestFieldAnswerValue?.toString()
          ),
        };

        localStorage.setItem(
          checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '').toString(),
          JSON.stringify(fieldAnswer)
        );

        if (triggerSubjectAction === 'setAction') {
          this.msfps.helper$.next({
            activeQuestionId: checkboxWrapperTarget.id
              .replace('Checkbox_WRAPPER_', '')
              .toString(),
            val: 'set on preview question input element',
          });
        }
      }
    });
    const modifiedHtmlStr = html.body.innerHTML;

    return modifiedHtmlStr;
  }

  deltaDate(input: Date, days = 0, months = 0, years = 0): Date {
    const y = input.getFullYear() + years;
    const m = input.getMonth() + months;
    const d = new Date(y, m, 1);
    const maxDay = new Date(y, m + 1, 0).getDate();
    d.setDate(Math.min(input.getDate(), maxDay));
    d.setDate(d.getDate() + days);
    return d;
  }

  toInputDate(d: Date): string {
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  }

  mapInputIntoUI(
    inputType: string,
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field.data[1].question.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('input')).forEach((eventTarget) => {

      const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(eventTarget.id.toString()) || 'null'
      );

      const answers: string[] = field.nodeLogic?.selectedAnswers as string[];

      const latestFieldAnswerValue = answers ?? null;

      // Declare isDateOutOfRange outside nested blocks so it's accessible in final validation
      let isDateOutOfRange = false;

      if (answers?.[0] && field.data[1].TypeValue != 'Attachment') {
        if (inputType === 'DATE') {
          const now = new Date(); now.setHours(0, 0, 0, 0);

          const minPastMonth = field.data[1].minPastMonth ?? 0;
          const maxFutureMonth = field.data[1].maxFutureMonth ?? 0;
          const isFutureDateAllowed = field.data[1].isFutureDateAllowed ?? true;

          let minDate: string | null = null;
          let maxDate: string | null = null;

          let config = '';
          if (eventTarget.hasAttribute('data-config')) {
            config = JSON.parse(eventTarget.getAttribute('data-config') || '""');
          }

          if (minPastMonth) {
            if (config) {
              minDate = this.toInputDate(this.deltaDate(now, -config, 0, 0));
            }
            else {
              minDate = this.toInputDate(this.deltaDate(now, 0, -Number(minPastMonth), 0));
            }
          }
          if (isFutureDateAllowed === false) {
            maxDate = null;
          } else if (maxFutureMonth) {
            maxDate = this.toInputDate(this.deltaDate(now, 0, Number(maxFutureMonth), 0));
          }

          // Only set minDate to today if maxDate exists and minDate is null
          if (!minDate && maxDate) {
            minDate = this.toInputDate(now);
          }

          const selectedDate = answers?.[0] ?? '';
          let rangeErrorMsg = '';
          if (selectedDate) {
            // Remove previous error message if any
            const prevErrorMsg = eventTarget.parentNode?.querySelector('[name="dateRangeErrorMsg"]');
            if (prevErrorMsg && eventTarget.parentNode) eventTarget.parentNode.removeChild(prevErrorMsg);

            // Compare as strings in "yyyy-MM-dd" format
            if(this.msfps.mode != 'previewDisabled'){
            if (minDate && selectedDate < minDate) {
              isDateOutOfRange = true;
              if (maxDate) {
                rangeErrorMsg = `Invalid date. Please refer to the guidance text and enter a valid date.`;
              } else {
                rangeErrorMsg = `Invalid date. Please refer to the guidance text and enter a valid date.`;
              }
            } else if (maxDate && selectedDate > maxDate) {
              isDateOutOfRange = true;
              if (minDate) {
                rangeErrorMsg = `Invalid date. Please refer to the guidance text and enter a valid date.`;
              } else {
                rangeErrorMsg = `Invalid date. Please refer to the guidance text and enter a valid date.`;
              }
            }
            }

            if (isDateOutOfRange) {
              const errorMsg = `<small style="display:block; color: crimson; margin-top: 0.5rem" name="dateRangeErrorMsg">
      <i class="fa fa-warning text-danger"></i> ${rangeErrorMsg}
    </small>`;
              eventTarget.parentElement?.insertAdjacentHTML('beforeend', errorMsg);
              field.valid = false;
            } else {
              field.valid = true;
            }
            eventTarget.setAttribute('value', selectedDate);
            eventTarget.value = selectedDate;;
          } else {
            eventTarget.value = '';
            eventTarget.removeAttribute('value');
            field.valid = true;
          }
        }else{
          eventTarget.setAttribute('value', answers?.[0]);
          eventTarget.value = answers?.[0];
        }
      } else {
        eventTarget.value = '';
        eventTarget.removeAttribute('value');
      }

      const inputValidationResult = this.vrs.validateInput(eventTarget);
      const invalidAnswerMsgHTML = inputValidationResult.messageHTML;

      const smallErrorMsgEle = eventTarget.parentNode?.children.namedItem(
        'invalidInputErrorMsg'
      );
      if (smallErrorMsgEle && eventTarget.parentNode)
        eventTarget.parentNode.removeChild(smallErrorMsgEle);

      if (eventTarget.value && !inputValidationResult.isInputValid) {
        eventTarget.parentElement?.insertAdjacentHTML(
          'beforeend',
          invalidAnswerMsgHTML
        );
      }

      if (this.msfps.mode === 'previewDisabled') {
        eventTarget.setAttribute('disabled', 'true');
        eventTarget.disabled = true;
      }

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      // Add note for FILE/Attachment fields if not already present
      if (inputType === 'FILE' || field.data[1].TypeValue === 'Attachment') {
        const parentEl = eventTarget.parentElement;
        // Check for 'Upload file' which matches both old ('Upload file accepts pdf') and new ('Upload files in PDF') wording
        if (parentEl && !parentEl.innerHTML.includes('Upload file')) {
          const noteDiv = document.createElement('div');
          noteDiv.setAttribute('style', 'margin-top: 8px; font-size: 13px; color: #333;');
          noteDiv.innerHTML = '<b>Note:</b> Upload files in PDF, Word, and Excel formats, up to 5 MB.';
          parentEl.appendChild(noteDiv);
        }
      }

      const formattedDates = inputType === 'DATE'
        ? latestFieldAnswerValue?.map(date => checkAndFormatDate(date))
        : latestFieldAnswerValue;

      let isDateInvalid = false;
      if (inputType === 'DATE' && formattedDates) {
        isDateInvalid = formattedDates.some(date => !date);
      }

      if (inputType === 'DATE' && isDateInvalid) {
        // Show the invalid date value in the message
        const invalidDateValue = (latestFieldAnswerValue && latestFieldAnswerValue[0]) ? latestFieldAnswerValue[0] : '';

        const errorContainer = document.createElement('small');
        errorContainer.style.display = 'block';
        errorContainer.style.color = 'crimson';
        errorContainer.style.marginTop = '0.5rem';
        errorContainer.setAttribute('name', 'invalidInputErrorMsg');

        // Add the warning icon (constant, not user-controlled)
        const iconWrapper = document.createElement('span');
        iconWrapper.innerHTML = '<i class="fa fa-warning text-danger"></i>';
        errorContainer.appendChild(iconWrapper);

        // Add the static text and the invalid date value as text (not HTML)
        errorContainer.appendChild(
          document.createTextNode(' The selected date ')
        );

        const boldDate = document.createElement('b');
        boldDate.textContent = invalidDateValue ?? '';
        errorContainer.appendChild(boldDate);

        errorContainer.appendChild(
          document.createTextNode(' is not valid. Please select from the calendar.')
        );

        if (eventTarget.parentElement) {
          eventTarget.parentElement.appendChild(errorContainer);
        }

        eventTarget.classList.add('ng-invalid');
        eventTarget.setAttribute('aria-invalid', 'true');
      } else if (inputType === 'DATE') {
        eventTarget.classList.remove('ng-invalid');
        eventTarget.removeAttribute('aria-invalid');
      }

      // Check if this is a file/attachment field
      const isFileOrAttachment = inputType === 'FILE' || field.data[1].TypeValue === 'Attachment';
      // For file/attachment fields, validate that there's at least one non-empty value
      const hasValidFileAnswer = latestFieldAnswerValue?.some(val => val && val.trim() !== '') ?? false;

      let isAnswerValid = inputType === 'DATE'
        ? !isDateInvalid && !isDateOutOfRange && inputValidationResult.isInputValid
        : isFileOrAttachment
          ? hasValidFileAnswer
          : inputValidationResult.isInputValid;

      if (inputType === 'DATE') {
        field.valid = !isDateInvalid && !isDateOutOfRange && inputValidationResult.isInputValid;
      } else if (isFileOrAttachment) {
        field.valid = hasValidFileAnswer;
      } else {
        field.valid = inputValidationResult.isInputValid;
      }

      const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: inputType === 'DATE' ? formattedDates : latestFieldAnswerValue,
        isAnswerValid,
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };
      localStorage.setItem(eventTarget.id, JSON.stringify(fieldAnswer));

      if (triggerSubjectAction === 'setAction') {
        this.msfps.helper$.next({
          activeQuestionId: eventTarget.id,
          val: 'set on preview question input element',
        });
      }
    });
    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  mapTextAreaInputIntoUI(
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('textarea')).forEach((eventTarget) => {

      const oldFieldAnswer: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
        localStorage.getItem(
          eventTarget.id.replace('textareaID_', '').toString()
        ) || 'null'
      );

      const answers: string[] = field.nodeLogic?.selectedAnswers as string[];

      const latestFieldAnswerValue = answers ?? null;

      if(answers?.[0]) {
        eventTarget.value = answers?.[0];
        eventTarget.setAttribute('value', answers?.[0]);
        eventTarget.innerHTML = answers?.[0];
        eventTarget.setAttribute('innerHTML', answers?.[0]);
        eventTarget.textContent = answers?.[0];
        eventTarget.setAttribute('textContent', answers?.[0]);
      }

      if (this.msfps.mode === 'previewDisabled') {
        eventTarget.setAttribute('disabled', 'true');
        eventTarget.disabled = true;
      }

      const modifiedFieldEle = document.createElement('div');
      modifiedFieldEle.setAttribute(
        'style',
        'display:flex;justify-content:left'
      );
      modifiedFieldEle.innerHTML = eventTarget.outerHTML;

      const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.sanitizer.bypassSecurityTrustHtml(
          modifiedFieldEle.outerHTML
        ),
        answerUniqueId: [eventTarget.id],
        answerValue: latestFieldAnswerValue,
        isAnswerValid: Boolean(eventTarget.value),
        isAnswerModified: !(
          oldFieldAnswer?.answerValue?.toString() ===
          latestFieldAnswerValue?.toString()
        ),
      };

      localStorage.setItem(
        eventTarget.id.replace('textareaID_', ''),
        JSON.stringify(fieldAnswer)
      );

      if (triggerSubjectAction === 'setAction') {
        this.msfps.helper$.next({
          activeQuestionId: eventTarget.id.replace('textareaID_', ''),
          val: 'set on preview question input element',
        });
      }
    });
    const modifiedHtmlStr = html.body.innerHTML;
    field.data[1].question.changingThisBreaksApplicationSecurity =
      modifiedHtmlStr;
    return modifiedHtmlStr;
  }

  // Remove answers
  clearResponseFromFieldUI(field: IQuesField) {
    // Look up: SPECEFIC FIELD TYPE LOGIC
    let modifiedHtmlStr;
    // map response into UI depending on the type of input field
    if (field?.data[1]?.type?.toLowerCase() === 'dropdown') {
      modifiedHtmlStr = this.clearDropdownInput('dropdown', field);

      //Reset the answers in field Obj
      field.data[1]?.dropdowndata?.map(
        (optData: IOptioningData) => (optData.selected = false)
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'multiselect-dropdown') {
      modifiedHtmlStr = this.clearDropdownInput('multiselect-dropdown', field);

      //Reset the answers in field Obj
      field.data[1]?.multiselectDropdowndata?.map(
        (optData: IOptioningData) => (optData.selected = false)
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'date') {
      modifiedHtmlStr = this.clearInput('DATE', field);
    } else if (field?.data[1]?.type?.toLowerCase() === 'textbox') {
      modifiedHtmlStr = this.clearInput('NUMBER', field);
    } else if (field?.data[1]?.type?.toLowerCase() === 'radio') {
      modifiedHtmlStr = this.clearRadioInput(field);

      //Reset the answers in field Obj
      field.data[1].radiodata?.map(
        (optData: IOptioningData) => (optData.selected = false)
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'checkbox') {
      modifiedHtmlStr = this.clearCheckboxInput(field);

      //Reset the answers in field Obj
      field.data[1].checkboxdata?.map(
        (optData: IOptioningData) => (optData.selected = false)
      );
    } else if (field?.data[1]?.type?.toLowerCase() === 'textarea') {
      modifiedHtmlStr = this.clearTextAreaInput(field);
    }
    return modifiedHtmlStr;
  }

  clearDropdownInput(
    dropdownType: 'dropdown' | 'multiselect-dropdown',
    field: IQuesField
  ) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('select'))?.forEach(
      (selectEleTarget) => {
        if (dropdownType === 'dropdown') {
          this.clearSingleSelectDropdownInput(selectEleTarget, field);
        } else if (dropdownType === 'multiselect-dropdown') {
          this.clearMultiSelectDropdownInput(selectEleTarget, field);
        }
      }
    );
    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  clearSingleSelectDropdownInput(
    eventTarget: HTMLSelectElement,
    field: IQuesField
  ) {
    localStorage.removeItem(eventTarget.id.replace('Dropdown_', ''));

    const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);

    optionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;
    });

    //apply selected attribute to the selected option

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = eventTarget.outerHTML;
  }

  clearMultiSelectDropdownInput(
    multiSelectTarget: HTMLSelectElement,
    field: IQuesField
  ) {
    localStorage.removeItem(
      multiSelectTarget.id.replace('MultiselectDropdown_', '')
    );
    const allOptionsArr = Array.from(
      multiSelectTarget.options as HTMLOptionsCollection
    );

    // remove select attribute for all the existing options
    allOptionsArr.forEach((option: HTMLOptionElement) => {
      option.removeAttribute('selected');
      option.selected = false;
    });

    const modifiedFieldEle = document.createElement('div');
    modifiedFieldEle.setAttribute('style', 'display:flex;justify-content:left');
    modifiedFieldEle.innerHTML = multiSelectTarget.outerHTML;
  }

  clearRadioInput(field: IQuesField) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field.data[1].question.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      localStorage.removeItem(eventTarget.name.replace('Radio_', ''));

      const selectedRadioEle = eventTarget;

      selectedRadioEle.checked = false;
      selectedRadioEle.removeAttribute('checked');
    });

    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  clearCheckboxInput(field: IQuesField) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
      const dynamicCheckboxLabelPrefix = /Checkbox_Label(\d+)_/g;

      const checkboxWrapperTarget =
        eventTarget?.parentElement?.parentElement?.parentElement;

      if (!checkboxWrapperTarget || !eventTarget.parentElement) {
        return;
      }

      checkboxWrapperTarget.setAttribute(
        'id',
        eventTarget.parentElement.id
          ?.replace(dynamicCheckboxLabelPrefix, 'Checkbox_WRAPPER_')
          ?.toString()
      );
      localStorage.removeItem(
        checkboxWrapperTarget.id.replace('Checkbox_WRAPPER_', '')
      );
      const allCheckboxContainersArr = Array.from(
        checkboxWrapperTarget.children
      );

      const allCheckboxesArr = [...allCheckboxContainersArr]?.map(
        (x) => x?.firstChild?.firstChild as HTMLInputElement
      );

      allCheckboxesArr?.forEach((optionEle: HTMLInputElement) => {
        optionEle.removeAttribute('checked');
        optionEle.checked = false;
      });
    });
    const modifiedHtmlStr = html.body.innerHTML;

    return modifiedHtmlStr;
  }

  clearInput(inputType: string, field: IQuesField) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field.data[1].question.changingThisBreaksApplicationSecurity,
      'text/html'
    );

    Array.from(html.getElementsByTagName('input')).forEach((eventTarget) => {

      localStorage.removeItem(eventTarget.id.replace('textboxID_', ''));

      localStorage.removeItem(eventTarget.id);
      eventTarget.removeAttribute('value');
      eventTarget.value = '';
    });
    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }

  clearTextAreaInput(field: IQuesField) {
    const parser = new DOMParser();
    const html = parser.parseFromString(
      field?.data[1]?.question?.changingThisBreaksApplicationSecurity,
      'text/html'
    );
    Array.from(html.getElementsByTagName('textarea')).forEach(
      (textareaInputEle) => {

        localStorage.removeItem(textareaInputEle.id.replace('textareaID_', ''));
        textareaInputEle.value = '';
        textareaInputEle.removeAttribute('value');
        textareaInputEle.innerHTML = '';
        textareaInputEle.removeAttribute('innerHTML');
        textareaInputEle.textContent = '';
        textareaInputEle.removeAttribute('textContent');
      }
    );
    const modifiedHtmlStr = html.body.innerHTML;
    return modifiedHtmlStr;
  }
}
